'use client';

import React from 'react';

import { useParams, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { BrowseProductsList } from '../BrowseProductsList';
import { Header } from '../Header';

import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Filters } from '@/components/Filters';
import { BrowseByTypes } from '@/modules/activityTracker/constants';
import { useCategoryBreadcrumbs } from '@/modules/breadcrumbs/useCategoryBreadcrumbs';
import { useBreadcrumbTrackingOnClick } from '@/modules/browse/hooks';
import { useBrowseTracking } from '@/modules/browse/useBrowseTracking';
import { type CategoryPageContent } from '@/modules/categories/getCategoryPageContent';
import { getPageHeaderText } from '@/modules/categories/helpers';
import { getCMSLocale } from '@/modules/cms/helpers/getCMSLocale';
import { useSearchId } from '@/modules/search/useSearchId';

export function Category({
  metadata: categoryPageMetadata,
  bubbles,
}: CategoryPageContent) {
  const t = useTranslations('common');
  const { category } = useParams<{ category: string[] }>();
  const params = useSearchParams();
  const categoryPathname = (category as string[]).join('/');
  const cmsLocale = getCMSLocale();
  const searchId = useSearchId();

  const { data: categoryBreadcrumbsRes } = useCategoryBreadcrumbs({
    categoryPathname,
    linkPrefix: '/category',
  });
  const breadcrumbItems = categoryBreadcrumbsRes || [];
  const onBreadcrumbClick = useBreadcrumbTrackingOnClick();
  const { title, description } = getPageHeaderText(
    category as string[],
    cmsLocale,
    categoryPageMetadata
  );
  const { handleProductClick } = useBrowseTracking({
    searchId,
    browseType: BrowseByTypes.CATEGORY,
    label: categoryPageMetadata?.title || '',
  });

  return (
    <>
      <Breadcrumbs
        items={breadcrumbItems}
        onBreadcrumbClick={onBreadcrumbClick}
      />
      <Header
        title={title}
        description={description || t('ShopCollection.Default')}
        bubbles={bubbles}
      />
      <Filters />
      <BrowseProductsList
        handleProductClick={handleProductClick}
        query={params.get('q') || ''}
      />
    </>
  );
}
